import { Injectable } from '@angular/core';
import { BehaviorSubject, filter } from 'rxjs';
import { take } from 'rxjs/operators';

import { FRONTEND_PERMISSIONS } from '../permissions.constants';

import { PermissionService } from './permission.service';

const DEMO_MODE_LS_KEY = 'karve-demo-mode';

@Injectable({ providedIn: 'root' })
export class DemoModeService {

    /**
     * Internal subject uses `null` as a sentinel for "initialising" — the
     * permission check has not resolved yet.  `isDemo$` only emits after the
     * first real value is published, so consumers (store effects using
     * combineLatest) are naturally gated until init completes.
     */
    private readonly _isDemo$ = new BehaviorSubject<boolean | null>(null);

    /** Emits `true` / `false` only after the permission check has resolved. */
    readonly isDemo$ = this._isDemo$.pipe(
        filter((v): v is boolean => v !== null),
    );

    /** Sync accessor — returns `false` while initialising. */
    get isDemo(): boolean {
        return this._isDemo$.value ?? false;
    }

    constructor(private permissionService: PermissionService) {
        // Restore the persisted preference only when the user actually has the
        // demo-mode permission.  Emitting the first real value here (rather than
        // from the BehaviorSubject constructor) gates all consumers until the
        // permission check resolves, preventing a brief live-data leak on refresh.
        this.permissionService.watchPermission(FRONTEND_PERMISSIONS.demoMode)
            .pipe(take(1))
            .subscribe(hasPermission => {
                if (hasPermission && localStorage.getItem(DEMO_MODE_LS_KEY) === 'true') {
                    this._isDemo$.next(true);
                } else {
                    if (!hasPermission) {
                        localStorage.removeItem(DEMO_MODE_LS_KEY);
                    }
                    this._isDemo$.next(false);
                }
            });
    }

    toggle(): void {
        const next = !this.isDemo;
        if (next) {
            this.enable();
        } else {
            this.disable();
        }
    }

    enable(): void {
        // Guard: only enable if the user has the demo-mode permission.
        this.permissionService.watchPermission(FRONTEND_PERMISSIONS.demoMode)
            .pipe(take(1))
            .subscribe(hasPermission => {
                if (!hasPermission) { return; }
                localStorage.setItem(DEMO_MODE_LS_KEY, 'true');
                this._isDemo$.next(true);
            });
    }

    disable(): void {
        localStorage.setItem(DEMO_MODE_LS_KEY, 'false');
        this._isDemo$.next(false);
    }
}
